import type { LinkProps, NavLinkProps } from '@remix-run/react';
import { Link, NavLink } from '@remix-run/react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon, type IconName } from '~/components/Icon';

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
export type CustomButtonProps = {
  iconStart?: IconName;
  iconEnd?: IconName;
  label?: string;
  element?: React.ElementType;
  htmlFor?: string;
};
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<ButtonVariantProps, 'type'>,
    CustomButtonProps {}
export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    Omit<ButtonVariantProps, 'type'> {
  icon: IconName;
  label: string;
}
export interface IconLinkButtonProps
  extends LinkProps,
    Omit<ButtonVariantProps, 'type'> {
  icon: IconName;
  label: string;
}
export type LinkButtonProps = Omit<ButtonVariantProps, 'type'> &
  CustomButtonProps &
  LinkProps;
export type NavLinkButtonProps = Omit<ButtonVariantProps, 'type'> &
  CustomButtonProps &
  Omit<NavLinkProps, 'children' | 'className'> & {
    children: React.ReactNode;
    className?: string;
  };

const buttonVariants = cva(
  [
    'rounded font-medium transition-colors duration-150 inline-flex items-center gap-3 text-center',
    'justify-center disabled:cursor-not-allowed disabled:opacity-50 ',
    'default-focus data-[highlighted]:ring-opacity-0',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-primary disabled:hover:bg-primary text-on-primary hover:bg-primary-hover  hover:text-on-primary-hover rdx-state-open:bg-primary-hover  rdx-state-open:text-on-primary-hover data-[active=true]:bg-primary-hover data-[active=true]:text-on-primary-hover',
        secondary:
          'bg-secondary/15 disabled:hover:bg-secondary/15  text-on-secondary hover:bg-secondary-hover  hover:text-on-secondary-hover rdx-state-open:bg-secondary-hover  rdx-state-open:text-on-secondary-hover data-[active=true]:bg-secondary-hover data-[active=true]:text-on-secondary-hover',
        tertiary:
          'bg-transparent disabled:hover:bg-transparent hover:bg-secondary/15 rdx-state-open:bg-bg-secondary-hover data-[active=true]:bg-secondary-hover',
        translucent:
          'dark:bg-white/20 dark:text-white dark:hover:enabled:bg-white/30 text-black bg-black/10 enabled:hover:bg-black/20',
        danger: 'bg-negative/10  text-negative hover:bg-negative/20 ',
        caution: 'bg-caution/15 text-caution hover:bg-caution/25',
      },
      size: {
        xs: 'text-xs max-h-7',
        sm: 'text-sm max-h-8',
        md: 'text-sm max-h-10',
        lg: 'text-md max-h-12',
      },
      type: {
        icon: '!flex-grow-0 aspect-square justify-center',
        default: '',
      },
    },
    compoundVariants: [
      {
        type: 'icon',
        size: 'xs',
        className: 'w-6 max-h-6',
      },
      {
        type: 'icon',
        size: 'sm',
        className: 'w-8',
      },
      {
        type: 'icon',
        size: 'md',
        className: 'w-10',
      },
      {
        type: 'icon',
        size: 'lg',
        className: 'w-14',
      },
      {
        variant: [
          'primary',
          'secondary',
          'tertiary',
          'translucent',
          'danger',
          'caution',
        ],
        type: 'default',
        size: 'xs',
        className: 'px-2 py-[0.8125rem] font-normal',
      },
      {
        variant: [
          'primary',
          'secondary',
          'tertiary',
          'translucent',
          'danger',
          'caution',
        ],
        type: 'default',
        size: 'sm',
        className: 'px-4 py-[0.8125rem]',
      },
      {
        variant: ['primary', 'secondary', 'tertiary', 'translucent', 'danger'],
        type: 'default',
        size: 'md',
        className: 'px-4 py-4',
      },
      {
        variant: ['primary', 'secondary', 'tertiary', 'translucent', 'danger'],
        type: 'default',
        size: 'lg',
        className: 'px-4 py-5',
      },
      {
        variant: ['secondary', 'tertiary'],
        type: 'icon',
        size: ['xs', 'sm', 'md', 'lg'],
        className: 'text-subdued hover:text-default',
      },
      {
        variant: ['tertiary'],
        type: 'icon',
        size: ['sm', 'md', 'lg'],
        className: 'hover:text-default rdx-state-open:text-default',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);
const remapIconSize = {
  xs: 'sm',
  sm: 'md',
  md: 'lg',
  lg: 'xl',
  action: 'lg',
} as const;
const iconMargins = {
  xs: { start: '-ms-0', end: '-me-0' },
  sm: { start: '-ms-0', end: '-me-0' },
  md: { start: '-ms-0', end: '-me-0' },
  lg: { start: '-ms-0', end: '-me-0' },
  action: { start: '', end: '' },
};
const InnerButton = ({
  iconStart,
  iconEnd,
  label,
  children,
  size,
}: Partial<ButtonProps>) => {
  return (
    <>
      {iconStart && (
        <Icon
          name={iconStart}
          size={remapIconSize[size || 'md']}
          className={iconMargins[size || 'md'].start}
        />
      )}
      {label ? <span>{label}</span> : children}
      {iconEnd && (
        <Icon
          name={iconEnd}
          size={remapIconSize[size || 'md']}
          className={iconMargins[size || 'md'].end}
        />
      )}
    </>
  );
};
export const Button = React.forwardRef(
  (
    {
      className,
      element: As = 'button',
      variant,
      size,
      label,
      iconStart,
      iconEnd,
      children,
      type = 'button',
      ...props
    }: ButtonProps,
    ref?: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const innerProps = { iconStart, iconEnd, label, children, size };
    return (
      <As
        ref={ref}
        className={twMerge(
          buttonVariants({ variant, size, type: 'default' }),
          className
        )}
        type={type}
        {...props}
      >
        <InnerButton {...innerProps} />
      </As>
    );
  }
);
Button.displayName = 'Button';

export const IconButton = React.forwardRef(
  (
    {
      className,
      icon,
      variant,
      size,
      label,
      type = 'button',
      ...props
    }: IconButtonProps,
    ref?: React.ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <button
        ref={ref}
        className={twMerge(
          buttonVariants({
            variant: variant || 'tertiary',
            size,
            type: 'icon',
          }),
          className
        )}
        aria-label={label}
        type={type}
        {...props}
      >
        <Icon name={icon} size={remapIconSize[size || 'md']} />
      </button>
    );
  }
);
IconButton.displayName = 'IconButton';

export const IconLinkButton = React.forwardRef(
  (
    {
      to,
      className,
      icon,
      variant,
      size,
      label,
      type = 'button',
      ...props
    }: IconLinkButtonProps,
    ref?: React.ForwardedRef<HTMLAnchorElement>
  ) => {
    return (
      <Link
        ref={ref}
        to={to}
        className={twMerge(
          buttonVariants({
            variant: variant || 'tertiary',
            size,
            type: 'icon',
          }),
          className
        )}
        aria-label={label}
        type={type}
        {...props}
      >
        <Icon name={icon} size={remapIconSize[size || 'md']} />
      </Link>
    );
  }
);
IconLinkButton.displayName = 'IconLinkButton';

export const LinkButton = React.forwardRef(
  (
    {
      to,
      variant,
      className,
      size,
      iconStart,
      iconEnd,
      label,
      children,
      ...rest
    }: LinkButtonProps,
    ref
  ) => {
    const innerProps = { iconStart, iconEnd, label, children, size };
    return (
      <Link
        to={to}
        className={twMerge(
          buttonVariants({ variant, size, type: 'default' }),
          className
        )}
        {...rest}
      >
        <InnerButton {...innerProps} />
      </Link>
    );
  }
);
LinkButton.displayName = 'LinkButton';

export const NavLinkButton = React.forwardRef(
  (
    {
      to,
      variant,
      className,
      size,
      iconStart,
      iconEnd,
      label,
      children,
      ...rest
    }: NavLinkButtonProps,
    ref
  ) => {
    const innerProps = { iconStart, iconEnd, label, children, size };
    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          twMerge(buttonVariants({ variant, size, type: 'default' }), className)
        }
        {...rest}
      >
        {({ isActive }) => <InnerButton {...innerProps} />}
      </NavLink>
    );
  }
);
NavLinkButton.displayName = 'NavLinkButton';
